import { useCallback, useEffect, useMemo, useState } from 'react';
import { ScrollTable } from '@/components/scroll-table';
import {
  LookalikeDimensionDetails,
  LookalikeFactorBreakdown,
  LookalikeSelection,
} from '@/components/lookalike-explainer';
import { lookalikeModelOf } from '@/lib/lookalike-model';
import { Icons } from '@/components/primitives';
import { SearchableDropdown } from '@/components/dropdowns';
import { QueryErrorNote } from '@/components/error-panel';
import {
  useAudiencePlannerBenchmarks,
  useAudiencePlannerPreview,
  useAudiences,
  useProduction,
  useProductions,
  useVenues,
} from '@/api/hooks';
import type {
  AudiencePlannerBenchmarks,
  AudiencePreview,
  AudiencePlannerPreviewInput,
} from '@/api/manage-hooks';
import type { RuleGroup } from '@/api/types';
import { fmtDateTime, fmtDay, withEt } from '@/lib/datetime';
import { audienceTypeFromVisualType } from '@/lib/audience-visual-rules';
import {
  AudienceBuilder,
  defaultGroupsForType,
  type AudiencePreviewState,
} from './audience';
import { PageActions } from '@/components/page-actions';

type VisualAudienceType = 'Direct' | 'Retargeting' | 'Lookalike';
type PreviewSnapshot = Pick<
  AudiencePreviewState,
  'count' | 'totalReach' | 'isPending' | 'error' | 'rules' | 'segments'
>;
type ProjectionResult = AudiencePreview['breakdowns'][number];
type DemographicInsight = Extract<
  ProjectionResult,
  { kind: 'by_age_range' }
>['insight'];

const EMPTY_PREVIEW: PreviewSnapshot = {
  count: null,
  totalReach: null,
  isPending: true,
  error: null,
  rules: null,
  segments: [],
};

export function buildAudiencePlannerRequest(
  type: VisualAudienceType,
  rules: RuleGroup,
): AudiencePlannerPreviewInput {
  return {
    audience_type: audienceTypeFromVisualType(type),
    rules,
  };
}

function definitionKey(
  type: VisualAudienceType,
  rules: RuleGroup | null,
): string | null {
  return rules
    ? JSON.stringify(buildAudiencePlannerRequest(type, rules))
    : null;
}

function resultOf<K extends ProjectionResult['kind']>(
  preview: AudiencePreview,
  kind: K,
): Extract<ProjectionResult, { kind: K }> | undefined {
  return preview.breakdowns.find(
    (result): result is Extract<ProjectionResult, { kind: K }> =>
      result.kind === kind,
  );
}

type BenchmarkProfile =
  NonNullable<AudiencePlannerBenchmarks['all_buyers']>;
type BenchmarkDimension =
  BenchmarkProfile['demographics'][number]['dimension'];
type DemographicBucket = NonNullable<DemographicInsight>['buckets'][number];

export function benchmarksForDisplay(
  benchmarks: AudiencePlannerBenchmarks | undefined,
  isPlaceholderData: boolean,
): AudiencePlannerBenchmarks | undefined {
  if (!benchmarks || !isPlaceholderData) return benchmarks;
  if (!benchmarks.all_buyers) return undefined;
  return {
    ...benchmarks,
    // A placeholder may contain the previously selected production. Keep its
    // all-buyers profile visible, but never label the old production as the
    // newly selected one.
    production_buyers: null,
  };
}

const COMPARISON_DIMENSIONS: ReadonlyArray<{
  dimension: BenchmarkDimension;
  title: string;
  projection:
    | 'by_age_range'
    | 'by_gender'
    | 'by_income'
    | 'by_education'
    | 'by_household_has_children';
}> = [
  { dimension: 'age', title: 'Age range', projection: 'by_age_range' },
  { dimension: 'gender', title: 'Gender', projection: 'by_gender' },
  { dimension: 'income', title: 'Household income', projection: 'by_income' },
  { dimension: 'education', title: 'Education', projection: 'by_education' },
  {
    dimension: 'household_has_children',
    title: 'Household has children',
    projection: 'by_household_has_children',
  },
];

function selectedDemographic(
  preview: AudiencePreview,
  projection: typeof COMPARISON_DIMENSIONS[number]['projection'],
): DemographicInsight | undefined {
  const result = preview.breakdowns.find((item) => item.kind === projection);
  return result && 'insight' in result
    ? result.insight as DemographicInsight
    : undefined;
}

function profileDemographic(
  profile: BenchmarkProfile | null,
  dimension: BenchmarkDimension,
): DemographicInsight | undefined {
  return profile?.demographics.find((item) => item.dimension === dimension)
    ?.insight;
}

function bucketKey(value: DemographicBucket['value']): string {
  return `${typeof value}:${String(value)}`;
}

interface AlignedComparisonBucket {
  key: string;
  label: string;
  order: number;
  planned?: number;
  plannedUsers?: number;
  production?: number;
  productionUsers?: number;
  allBuyers?: number;
  allBuyersUsers?: number;
}

export function alignComparisonBuckets(
  planned: DemographicInsight | undefined,
  production: DemographicInsight | undefined,
  allBuyers: DemographicInsight | undefined,
): AlignedComparisonBucket[] {
  const insights = [planned, production, allBuyers];
  const sources = insights.map((insight) =>
    insight?.status === 'ready' ? insight.buckets : []);
  const rows = new Map<string, AlignedComparisonBucket>();

  sources.forEach((buckets, sourceIndex) => {
    buckets.forEach((bucket) => {
      const key = bucketKey(bucket.value);
      const current = rows.get(key) ?? {
        key,
        label: bucket.display_label,
        order: bucket.order,
      };
      current.order = Math.min(current.order, bucket.order);
      if (sourceIndex === 0) {
        current.planned = bucket.percentage;
        current.plannedUsers = bucket.estimated_users;
      }
      if (sourceIndex === 1) {
        current.production = bucket.percentage;
        current.productionUsers = bucket.estimated_users;
      }
      if (sourceIndex === 2) {
        current.allBuyers = bucket.percentage;
        current.allBuyersUsers = bucket.estimated_users;
      }
      rows.set(key, current);
    });
  });

  return [...rows.values()]
    .map((row) => ({
      ...row,
      // A ready live insight may omit zero-valued buckets, whereas daily
      // profiles carry the complete catalog. Missing means zero only for a
      // source that explicitly declared itself ready.
      planned: row.planned ?? (
        planned?.status === 'ready' ? 0 : undefined
      ),
      plannedUsers: row.plannedUsers ?? (
        planned?.status === 'ready' ? 0 : undefined
      ),
      production: row.production ?? (
        production?.status === 'ready' ? 0 : undefined
      ),
      productionUsers: row.productionUsers ?? (
        production?.status === 'ready' ? 0 : undefined
      ),
      allBuyers: row.allBuyers ?? (
        allBuyers?.status === 'ready' ? 0 : undefined
      ),
      allBuyersUsers: row.allBuyersUsers ?? (
        allBuyers?.status === 'ready' ? 0 : undefined
      ),
    }))
    .sort((left, right) =>
      left.order - right.order || left.label.localeCompare(right.label));
}

export function unambiguousProductionId(rules: RuleGroup): string | null {
  const ids = new Set<string>();
  const visit = (group: RuleGroup) => {
    group.rules.forEach((rule) => {
      if (rule.kind === 'purchase_history' || rule.kind === 'purchase_history_between') {
        rule.production_ids.forEach((id) => ids.add(id));
      } else if (rule.kind === 'group') {
        visit(rule);
      }
    });
  };
  visit(rules);
  return ids.size === 1 ? [...ids][0]! : null;
}

function formatPercentage(value: number | undefined): string {
  return value === undefined ? '—' : `${value.toFixed(1)}%`;
}

function formatDelta(
  planned: number | undefined,
  comparator: number | undefined,
): string | null {
  if (planned === undefined || comparator === undefined) return null;
  const delta = planned - comparator;
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
  return `${sign}${Math.abs(delta).toFixed(1)} pp`;
}

function PercentageCell({
  value,
  estimatedUsers,
  series,
  label,
}: {
  value: number | undefined;
  estimatedUsers: number | undefined;
  series: 'planned' | 'production' | 'all';
  label: string;
}) {
  const accessibleValue = value === undefined
    ? `${label}: unavailable`
    : `${label}: ${formatPercentage(value)}, approximately ${(estimatedUsers ?? 0).toLocaleString()} users`;
  return (
    <td className="audience-comparison-value">
      <span className="tnum" aria-label={accessibleValue}>
        {formatPercentage(value)}
      </span>
      {estimatedUsers !== undefined && (
        <small className="audience-comparison-estimate" aria-hidden="true">
          ≈ {estimatedUsers.toLocaleString()} users
        </small>
      )}
      {value !== undefined && (
        <span className="audience-comparison-mini-track" aria-hidden="true">
          <span
            className={`is-${series}`}
            style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
          />
        </span>
      )}
    </td>
  );
}

interface Standout {
  key: string;
  magnitude: number;
  text: string;
}

export function comparisonStandouts(
  preview: AudiencePreview,
  benchmarks: AudiencePlannerBenchmarks,
  productionName: string | null,
): Standout[] {
  const candidates: Standout[] = [];
  const comparators: Array<{
    profile: BenchmarkProfile | null;
    label: string;
    field: 'production' | 'allBuyers';
  }> = [
    {
      profile: benchmarks.production_buyers,
      label: productionName ? `buyers of ${productionName}` : 'production buyers',
      field: 'production',
    },
    {
      profile: benchmarks.all_buyers,
      label: 'all buyers',
      field: 'allBuyers',
    },
  ];

  COMPARISON_DIMENSIONS.forEach(({ dimension, title, projection }) => {
    const planned = selectedDemographic(preview, projection);
    comparators.forEach(({ profile, label, field }) => {
      const rows = alignComparisonBuckets(
        planned,
        field === 'production'
          ? profileDemographic(profile, dimension)
          : undefined,
        field === 'allBuyers'
          ? profileDemographic(profile, dimension)
          : undefined,
      );
      rows.forEach((row) => {
        const comparator = field === 'production'
          ? row.production
          : row.allBuyers;
        if (row.planned === undefined || comparator === undefined) return;
        const delta = row.planned - comparator;
        if (Math.abs(delta) < 3) return;
        candidates.push({
          key: `${dimension}:${row.key}:${field}`,
          magnitude: Math.abs(delta),
          text: `${title}: ${row.label} is ${Math.abs(delta).toFixed(1)} percentage points ${delta >= 0 ? 'above' : 'below'} ${label}.`,
        });
      });
    });
  });

  return candidates
    .sort((left, right) => right.magnitude - left.magnitude)
    .slice(0, 3);
}

function SeriesHeading({
  series,
  children,
}: {
  series: 'planned' | 'production' | 'all';
  children: string;
}) {
  return (
    <span className="audience-comparison-series-heading">
      <span
        className={`audience-comparison-series-mark is-${series}`}
        aria-hidden="true"
      />
      <span>{children}</span>
    </span>
  );
}

function ComparisonDimension({
  title,
  planned,
  production,
  allBuyers,
  productionName,
}: {
  title: string;
  planned: DemographicInsight | undefined;
  production: DemographicInsight | undefined;
  allBuyers: DemographicInsight | undefined;
  productionName: string | null;
}) {
  const rows = alignComparisonBuckets(planned, production, allBuyers);
  const ready = planned?.status === 'ready'
    && (production?.status === 'ready' || allBuyers?.status === 'ready')
    && rows.length > 0;
  return (
    <section className="panel audience-comparison-dimension">
      <div className="panel-hd">
        <div>
          <h3>{title}</h3>
          <div className="audience-planner-panel-sub">
            Share of each audience in the same filter buckets
          </div>
        </div>
      </div>
      <div className="panel-body">
        {!ready ? (
          <div className="audience-planner-empty">
            Not enough data to compare this breakdown.
          </div>
        ) : (
          <ScrollTable className="audience-comparison-table-scroll" label="Audience comparison metrics" identityColumn={1}>
            <table className="audience-comparison-table">
              <caption className="sr-only">
                {title} comparison for your planned audience, selected
                production buyers, and all buyers
              </caption>
              <thead>
                <tr>
                  <th scope="col">Bucket</th>
                  <th scope="col">
                    <SeriesHeading series="planned">Your audience</SeriesHeading>
                  </th>
                  {productionName && (
                    <th scope="col">
                      <SeriesHeading series="production">
                        {`Buyers: ${productionName}`}
                      </SeriesHeading>
                    </th>
                  )}
                  <th scope="col">
                    <SeriesHeading series="all">All buyers</SeriesHeading>
                  </th>
                  <th scope="col">Difference</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const productionDelta = productionName
                    ? formatDelta(row.planned, row.production)
                    : null;
                  const allDelta = formatDelta(row.planned, row.allBuyers);
                  return (
                    <tr key={row.key}>
                      <th scope="row">{row.label}</th>
                      <PercentageCell
                        value={row.planned}
                        estimatedUsers={row.plannedUsers}
                        series="planned"
                        label={`${row.label}, your audience`}
                      />
                      {productionName && (
                        <PercentageCell
                          value={row.production}
                          estimatedUsers={row.productionUsers}
                          series="production"
                          label={`${row.label}, ${productionName} buyers`}
                        />
                      )}
                      <PercentageCell
                        value={row.allBuyers}
                        estimatedUsers={row.allBuyersUsers}
                        series="all"
                        label={`${row.label}, all buyers`}
                      />
                      <td className="audience-comparison-deltas">
                        {productionDelta && (
                          <span>
                            {productionDelta}
                            <small>vs production</small>
                          </span>
                        )}
                        {allDelta && (
                          <span>
                            {allDelta}
                            <small>vs all buyers</small>
                          </span>
                        )}
                        {!productionDelta && !allDelta && <span>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ScrollTable>
        )}
      </div>
    </section>
  );
}

export function AudiencePlannerComparison({
  preview,
  benchmarks,
  isLoading,
  error,
  productionId,
  productionName,
  productionOptions,
  onProductionSearchChange,
  onProductionChange,
  onRetry,
}: {
  preview: AudiencePreview;
  benchmarks: AudiencePlannerBenchmarks | undefined;
  isLoading: boolean;
  error: Error | null;
  productionId: string | null;
  productionName: string | null;
  productionOptions: Array<{ id: string; label: string; sublabel?: string }>;
  onProductionSearchChange: (query: string) => void;
  onProductionChange: (productionId: string | null) => void;
  onRetry: () => void;
}) {
  const standouts = benchmarks
    ? comparisonStandouts(preview, benchmarks, productionName)
    : [];
  const productionMissing = !!productionId
    && !!benchmarks
    && !isLoading
    && benchmarks.freshness !== 'unavailable'
    && !benchmarks.production_buyers;

  return (
    <section
      className="audience-comparison"
      aria-labelledby="audience-comparison-title"
      aria-busy={isLoading}
    >
      <div className="audience-comparison-heading">
        <div>
          <span>Daily buyer benchmarks</span>
          <strong id="audience-comparison-title">Compare your audience</strong>
          <p>
            See how this plan differs from buyers of one production and from
            all buyers. Percentages describe composition, not likelihood or causation.
          </p>
        </div>
        <div className="audience-comparison-picker">
          <span>Production benchmark</span>
          <SearchableDropdown
            value={productionId ?? ''}
            options={productionOptions}
            onChange={(value: string) => onProductionChange(value || null)}
            onSearchChange={onProductionSearchChange}
            emptyOptionLabel="No production selected"
            emptyOptionValue=""
            placeholder="Select a production"
            searchPlaceholder="Search productions…"
            triggerClassName="audience-comparison-picker-trigger"
            ariaLabel={
              productionName
                ? `Production buyer benchmark: ${productionName}`
                : 'Production buyer benchmark: none selected'
            }
            popWidth={380}
          />
        </div>
      </div>

      {isLoading && !benchmarks && (
        <div
          className="panel audience-comparison-state"
          role="status"
          aria-live="polite"
        >
          <span className="audience-estimate-spinner" aria-hidden="true" />
          <div>
            <strong>Loading daily comparisons…</strong>
            <span>Your audience analysis is complete; only the saved benchmarks are loading.</span>
          </div>
        </div>
      )}

      {error && (
        <div className="panel audience-comparison-state is-error">
          <QueryErrorNote
            error={error}
            what="daily audience comparisons"
            onRetry={onRetry}
          />
        </div>
      )}

      {benchmarks?.freshness === 'unavailable' && !error && (
        <div className="panel audience-comparison-state">
          <div>
            <strong>Daily comparisons are not available yet</strong>
            <span>
              Your audience results are unaffected. Try again after the next
              benchmark refresh.
            </span>
          </div>
          <button className="btn btn-sm" type="button" onClick={onRetry}>
            Retry
          </button>
        </div>
      )}

      {benchmarks && benchmarks.freshness !== 'unavailable' && (
        <>
          <div
            className={`audience-comparison-freshness ${benchmarks.freshness === 'stale' ? 'is-stale' : ''}`}
            role={benchmarks.freshness === 'stale' ? 'status' : undefined}
          >
            <span>
              {benchmarks.freshness === 'stale'
                ? 'Snapshot older than expected'
                : 'Updated daily'}
            </span>
            <strong>
              {benchmarks.computed_at
                ? withEt(fmtDateTime(benchmarks.computed_at))
                : 'Update time unavailable'}
            </strong>
            {benchmarks.window_start && benchmarks.window_end && (
              <small>
                Buyer window: {fmtDay(benchmarks.window_start)}–{fmtDay(benchmarks.window_end)}
              </small>
            )}
          </div>

          {/* Benchmarks are computed only for productions that have ticket
              buyers in the trailing window, so this is the ordinary case for a
              production that has not gone on sale — not a fault to apologise
              for. */}
          {productionMissing && (
            <div className="audience-comparison-inline-note" role="status">
              This production has no buyer history to compare against yet.
              All-buyers comparisons remain available.
            </div>
          )}

          {isLoading && productionId && (
            <div className="audience-comparison-inline-note" role="status">
              Loading the selected production benchmark. All-buyers comparisons
              remain visible while it updates.
            </div>
          )}

          {standouts.length > 0 && (
            <section className="panel audience-comparison-standouts">
              <div>
                <span className="audience-planner-kicker">What stands out</span>
                <strong>Largest composition differences</strong>
              </div>
              <ol>
                {standouts.map((item) => (
                  <li key={item.key}>{item.text}</li>
                ))}
              </ol>
            </section>
          )}

          <div className="audience-comparison-dimensions">
            {COMPARISON_DIMENSIONS.map(({ dimension, title, projection }) => (
              <ComparisonDimension
                key={dimension}
                title={title}
                planned={selectedDemographic(preview, projection)}
                production={profileDemographic(
                  benchmarks.production_buyers,
                  dimension,
                )}
                allBuyers={profileDemographic(
                  benchmarks.all_buyers,
                  dimension,
                )}
                productionName={
                  benchmarks.production_buyers ? productionName : null
                }
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

interface InsightRow {
  key: string;
  label: string;
  users: number;
}

function InsightBreakdown({
  title,
  subtitle,
  rows,
  total,
  empty,
}: {
  title: string;
  subtitle: string;
  rows: InsightRow[];
  total: number;
  empty: string;
}) {
  const visibleRows = rows.slice(0, 10);
  return (
    <section className="panel audience-planner-breakdown">
      <div className="panel-hd">
        <div>
          <h3>{title}</h3>
          <div className="audience-planner-panel-sub">{subtitle}</div>
        </div>
      </div>
      <div className="panel-body">
        {visibleRows.length === 0 ? (
          <div className="audience-planner-empty">{empty}</div>
        ) : (
          <div className="audience-planner-bars">
            {visibleRows.map((row) => {
              const percentage = total > 0 ? (row.users / total) * 100 : 0;
              return (
                <div className="audience-planner-bar-row" key={row.key}>
                  <div className="audience-planner-bar-meta">
                    <span title={row.label}>{row.label}</span>
                    <strong className="tnum">
                      {row.users.toLocaleString()}
                      <small>{percentage.toFixed(1)}%</small>
                    </strong>
                  </div>
                  <div className="audience-planner-bar-track" aria-hidden="true">
                    <span style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}/>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

export function AudiencePlannerInsights({
  preview,
  isLoading,
  error,
  isStale,
  onRetry,
  venueNames = {},
  audienceNames = {},
  comparison,
}: {
  preview: AudiencePreview | undefined;
  isLoading: boolean;
  error: Error | null;
  isStale: boolean;
  onRetry: () => void;
  venueNames?: Record<string, string>;
  audienceNames?: Record<string, string>;
  comparison?: Omit<
    Parameters<typeof AudiencePlannerComparison>[0],
    'preview'
  >;
}) {
  if (!preview && isLoading) {
    return (
      <section
        className="panel audience-planner-analysis-state"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <span className="audience-estimate-spinner" aria-hidden="true"/>
        <div>
          <strong>Analyzing audience…</strong>
          <span>Building reach, purchase, location, and seed insights.</span>
        </div>
      </section>
    );
  }

  if (!preview && error) {
    return (
      <section className="panel audience-planner-analysis-error">
        <QueryErrorNote
          error={error}
          what="audience insights"
          onRetry={onRetry}
        />
      </section>
    );
  }

  if (!preview) {
    return (
      <section className="panel audience-planner-analysis-empty">
        <span className="audience-planner-empty-icon">{Icons.reports}</span>
        <div>
          <strong>Insights appear after analysis</strong>
          <span>Build the audience above, then select Analyze audience.</span>
        </div>
      </section>
    );
  }

  const count = preview.count;
  const universe = preview.total_reach;
  const percent = universe > 0 ? (count / universe) * 100 : null;
  const recency = resultOf(preview, 'by_recency_band');
  const venues = resultOf(preview, 'by_venue');
  const zip = resultOf(preview, 'by_zip_prefix');
  const seeds = resultOf(preview, 'by_audience_type_seed');

  const recencyRows: InsightRow[] = (recency?.buckets ?? []).map((bucket) => ({
    key: bucket.band,
    label: bucket.band,
    users: bucket.users,
  }));
  const venueRows: InsightRow[] = (venues?.buckets ?? []).map((bucket) => ({
    key: bucket.venue_id,
    label: venueNames[bucket.venue_id] ?? bucket.venue_id,
    users: bucket.users,
  }));
  const zipRows: InsightRow[] = (zip?.buckets ?? []).map((bucket) => ({
    key: bucket.zip_prefix,
    label: bucket.zip_prefix,
    users: bucket.users,
  }));
  const seedRows: InsightRow[] = (seeds?.buckets ?? []).map((bucket) => ({
    key: bucket.seed_audience_id,
    label: audienceNames[bucket.seed_audience_id] ?? bucket.seed_audience_id,
    users: bucket.users,
  }));

  // Read off the same preview response the rest of this screen renders — the explainer
  // performs no request of its own, because the server answers a prospecting tree entirely
  // from the published nightly fit.
  const lookalikeModel = lookalikeModelOf(preview.breakdowns);
  const lookalikeModelPending = lookalikeModel !== null
    && lookalikeModel.status !== 'ready';

  return (
    <div
      className={`audience-planner-results ${isStale ? 'is-stale' : ''}`}
      aria-live="polite"
    >
      {isStale && (
        <div className="audience-planner-stale" role="status">
          <span>{Icons.refresh}</span>
          <div>
            <strong>Plan changed</strong>
            <span>These insights describe the previous definition. Analyze again to update them.</span>
          </div>
        </div>
      )}

      {error && (
        <QueryErrorNote
          error={error}
          what="updated audience insights"
          onRetry={onRetry}
        />
      )}

      <section className="panel audience-planner-summary">
        <div className="audience-planner-summary-main">
          <span className="audience-planner-kicker">
            {lookalikeModelPending ? 'Lookalike audience size' : 'Matched users'}
          </span>
          <strong className="tnum">
            {lookalikeModelPending ? 'Pending' : count.toLocaleString()}
          </strong>
          <span>
            {lookalikeModelPending
              ? 'Waiting for the first published fit; this is not a measured empty audience'
              : percent === null
              ? 'Share of available audience universe unavailable'
              : `${percent.toFixed(1)}% of the available audience universe`}
          </span>
        </div>
        <div className="audience-planner-summary-stat">
          <span>Available audience universe</span>
          <strong className="tnum">{universe.toLocaleString()}</strong>
        </div>
        <div className="audience-planner-summary-stat">
          <span>Analysis updated</span>
          <strong>{withEt(fmtDateTime(preview.computed_at))}</strong>
        </div>
      </section>

      {count > 0 && !lookalikeModelPending && comparison && (
        <AudiencePlannerComparison preview={preview} {...comparison} />
      )}

      {lookalikeModelPending && lookalikeModel ? (
        <section className="panel audience-planner-breakdown">
          <div className="panel-hd">
            <div>
              <h3>Lookalike model</h3>
              <div className="audience-planner-panel-sub">
                The audience can be planned after its seed model is fitted
              </div>
            </div>
          </div>
          <div className="panel-body">
            <LookalikeSelection
              model={lookalikeModel}
              shareBps={lookalikeModel.selection.share_bps}
            />
          </div>
        </section>
      ) : count === 0 ? (
        <section className="panel audience-planner-zero">
          <strong>No users match this definition</strong>
          <span>Broaden or remove a rule, then analyze the updated plan.</span>
        </section>
      ) : (
        <>
          <div className="audience-planner-section-heading">
            <span>Audience behavior</span>
            <strong>Purchases, places, and saved-audience context</strong>
          </div>
          <div className="audience-planner-insight-grid">
            <InsightBreakdown
              title="Purchase recency"
              subtitle="Latest purchase among transactions used for this analysis"
              rows={recencyRows}
              total={count}
              empty="No purchase recency data is available for this audience."
            />
            <InsightBreakdown
              title="Venues"
              subtitle="Venues represented in the analyzed transactions"
              rows={venueRows}
              total={count}
              empty="No venue data is available for this audience."
            />
            <InsightBreakdown
              title="Residence area"
              subtitle={`ZIP${zip?.len ?? 3} prefixes for matched users`}
              rows={zipRows}
              total={count}
              empty="No residence-area data is available for this audience."
            />
            {/*
              `by_audience_type_seed` read `lookalike_lookup`, a table that never
              existed in any schema, so this panel could not render. It is kept
              only for a saved audience whose stored definition still carries the
              old projection; the lookalike explainer below replaced it.
            */}
            {seedRows.length > 0 && (
              <InsightBreakdown
                title="Saved audience seed"
                subtitle="Seed audiences used by this definition"
                rows={seedRows}
                total={count}
                empty="No saved-audience seed applies to this definition."
              />
            )}
            {lookalikeModel && (
              <section className="panel audience-planner-breakdown">
                <div className="panel-hd">
                  <div>
                    <h3>How this lookalike was assembled</h3>
                    <div className="audience-planner-panel-sub">
                      Which factors separated the seed audience from everyone else, and by how
                      much
                    </div>
                  </div>
                </div>
                <div className="panel-body" style={{ display: 'grid', gap: 16 }}>
                  <LookalikeSelection
                    model={lookalikeModel}
                    shareBps={lookalikeModel.selection.share_bps}
                  />
                  <LookalikeFactorBreakdown model={lookalikeModel} />
                  <LookalikeDimensionDetails model={lookalikeModel} />
                </div>
              </section>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function AudiencePlanner() {
  const [type, setType] = useState<VisualAudienceType>('Direct');
  const [groups, setGroups] = useState(() => defaultGroupsForType('Direct'));
  const [preview, setPreview] = useState<PreviewSnapshot>(EMPTY_PREVIEW);
  const [analysisRequest, setAnalysisRequest] =
    useState<AudiencePlannerPreviewInput | null>(null);
  const [analyzedKey, setAnalyzedKey] = useState<string | null>(null);
  const [comparisonProductionId, setComparisonProductionId] =
    useState<string | null>(null);
  const [comparisonSelectionIsManual, setComparisonSelectionIsManual] =
    useState(false);
  const [comparisonProductionSearch, setComparisonProductionSearch] =
    useState('');
  const [resetVersion, setResetVersion] = useState(0);
  const analysis = useAudiencePlannerPreview(analysisRequest);
  const audiences = useAudiences().data ?? [];
  const productionsQuery = useProductions(
    comparisonProductionSearch
      ? { q: comparisonProductionSearch }
      : {},
  );
  const productions = productionsQuery.data ?? [];
  const selectedComparisonProduction = useProduction(
    comparisonProductionId ?? '',
  );
  const venues = useVenues().data ?? [];
  const suggestedProductionId = useMemo(
    () => analysisRequest
      ? unambiguousProductionId(analysisRequest.rules)
      : null,
    [analysisRequest],
  );

  useEffect(() => {
    if (!comparisonSelectionIsManual) {
      setComparisonProductionId(suggestedProductionId);
    }
  }, [comparisonSelectionIsManual, suggestedProductionId]);

  const benchmarks = useAudiencePlannerBenchmarks(
    comparisonProductionId,
    !!analysis.data && analysis.data.count > 0,
  );
  const displayedBenchmarks = useMemo(
    () => benchmarksForDisplay(
      benchmarks.data,
      benchmarks.isPlaceholderData,
    ),
    [benchmarks.data, benchmarks.isPlaceholderData],
  );

  const currentKey = definitionKey(type, preview.rules);
  const stale = analyzedKey !== null && currentKey !== analyzedKey;
  const venueNames = useMemo(
    () => Object.fromEntries(
      venues.map((venue) => [venue.id, venue.name || venue.id]),
    ),
    [venues],
  );
  const audienceNames = useMemo(
    () => Object.fromEntries(
      audiences.map((audience) => [audience.id, audience.name]),
    ),
    [audiences],
  );
  const productionOptions = useMemo(
    () => {
      const selected = selectedComparisonProduction.data;
      const available = selected
        && !productions.some((production) => production.id === selected.id)
        ? [selected, ...productions]
        : productions;
      return available.map((production) => ({
        id: production.id,
        label: production.name,
        sublabel: production.genre ?? production.source_reference,
      }));
    },
    [productions, selectedComparisonProduction.data],
  );
  const comparisonProductionName = useMemo(
    () => comparisonProductionId
      ? selectedComparisonProduction.data?.name ?? productions.find(
        (production) => production.id === comparisonProductionId,
      )?.name ?? comparisonProductionId
      : null,
    [comparisonProductionId, productions, selectedComparisonProduction.data?.name],
  );

  const onPreviewChange = useCallback((snapshot: PreviewSnapshot) => {
    setPreview((current) => (
      current.count === snapshot.count
      && current.totalReach === snapshot.totalReach
      && current.isPending === snapshot.isPending
      && current.error === snapshot.error
      && current.rules === snapshot.rules
      && current.segments === snapshot.segments
        ? current
        : snapshot
    ));
  }, []);

  const analyze = useCallback(() => {
    if (!preview.rules) return;
    const nextKey = definitionKey(type, preview.rules);
    if (nextKey === analyzedKey && analysisRequest) {
      void analysis.refetch();
      return;
    }
    setAnalyzedKey(nextKey);
    setAnalysisRequest(buildAudiencePlannerRequest(type, preview.rules));
  }, [analysis, analysisRequest, analyzedKey, preview.rules, type]);

  const reset = useCallback(() => {
    setType('Direct');
    setGroups(defaultGroupsForType('Direct'));
    setPreview(EMPTY_PREVIEW);
    setAnalysisRequest(null);
    setAnalyzedKey(null);
    setComparisonProductionId(null);
    setComparisonSelectionIsManual(false);
    setComparisonProductionSearch('');
    setResetVersion((version) => version + 1);
  }, []);

  const selectComparisonProduction = useCallback((productionId: string | null) => {
    setComparisonSelectionIsManual(true);
    setComparisonProductionId(productionId);
  }, []);

  const analysisCurrent = !!analysis.data && !stale && !analysis.isError;

  return (
    <div>
      <div className="page-hd">
        <div>
          <h1 className="page-title">Audience Planner</h1>
          <div className="page-sub">Explore audience definitions without saving them</div>
        </div>
        <PageActions>
          <button className="btn btn-ghost" data-phone="inline" onClick={reset}>
            {Icons.refresh} <span className="phone-long">Reset plan</span><span className="phone-short">Reset</span>
          </button>
        </PageActions>
      </div>

      <div className="audience-planner-scroll">
        <div className="audience-planner-canvas">
          <div className="audience-planner-notice">
            <span className="audience-planner-notice-icon">{Icons.search}</span>
            <div>
              <strong>Planning workspace</strong>
              <span>
                Explore and analyze freely. This definition is not saved, and
                nothing is sent to an advertising destination.
              </span>
            </div>
          </div>

          <section className="panel audience-planner-builder">
            <div className="panel-hd">
              <div>
                <h3>Audience definition</h3>
                <div className="audience-planner-panel-sub">
                  Groups combine as OR; rules within each group combine as AND.
                </div>
              </div>
              <span className="ps ps-idle">PLAN ONLY</span>
            </div>
            <div className="panel-body">
              <AudienceBuilder
                key={resetVersion}
                production={null}
                confirmed={analysisCurrent}
                type={type}
                setType={setType}
                groups={groups}
                setGroups={setGroups}
                hideSavedPicker
                onPreviewChange={onPreviewChange}
                onConfirm={analyze}
                actionMode="analyze"
                actionBusy={analysis.isFetching && !stale}
              />
            </div>
          </section>

          <div className="audience-planner-analysis-heading">
            <div>
              <span>Audience insights</span>
              <strong>Understand the people in this plan</strong>
            </div>
            {preview.segments.length > 1 && (
              <span className="tag">
                {preview.segments.length} audience groups
              </span>
            )}
          </div>

          <AudiencePlannerInsights
            preview={analysis.data}
            isLoading={analysis.isFetching}
            error={analysis.error}
            isStale={stale}
            onRetry={() => void analysis.refetch()}
            venueNames={venueNames}
            audienceNames={audienceNames}
            comparison={{
              benchmarks: displayedBenchmarks,
              isLoading: benchmarks.isFetching,
              error: benchmarks.error,
              productionId: comparisonProductionId,
              productionName: comparisonProductionName,
              productionOptions,
              onProductionSearchChange: setComparisonProductionSearch,
              onProductionChange: selectComparisonProduction,
              onRetry: () => void benchmarks.refetch(),
            }}
          />
        </div>
      </div>
    </div>
  );
}
